import { useTranslation } from "react-i18next";
import { ArrowRight, Check, Video, Hotel, Building2, HeartPulse, HandHeart } from "lucide-react";
import { Link } from "@/i18n/routing";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { ServiceCard } from "@/components/Cards";
import { IndependenceStrip } from "@/components/IndependenceStrip";
import { EmergencyNotice } from "@/components/EmergencyNotice";
import { CTAButtons } from "@/components/CTAButtons";
import { URGENT_SERVICES } from "@/data/site";

const EXAMPLES = [
  "Fever",
  "Food poisoning",
  "Vomiting",
  "Diarrhoea",
  "Dehydration",
  "Infections",
  "Minor injuries",
  "Pain",
  "Rashes",
  "Ear problems",
  "Respiratory symptoms",
];

const SUPPORT_OPTIONS = [
  { icon: Video, title: "Online doctor consultation" },
  { icon: Hotel, title: "Doctor-to-hotel visit" },
  { icon: Building2, title: "Clinic appointment" },
  { icon: HeartPulse, title: "Hospital coordination" },
  { icon: HandHeart, title: "Nursing support" },
];

const UrgentCare = () => {
  const { t } = useTranslation();
  return (
    <>
      <Seo
        title={t("pages.urgentSupport.seoTitle")}
        description={t("pages.urgentSupport.metaDescription")}
        path="/urgent-care"
        exact
      />
      <PageHero
        eyebrow={t("urgent.eyebrow")}
        title={t("urgent.h1")}
        subtitle={t("urgent.intro")}
        crumbs={[{ label: t("nav.home"), to: "/" }, { label: t("nav.urgentCare") }]}
      >
        <CTAButtons />
      </PageHero>

      <section className="vc-container py-14 sm:py-16">
        <Reveal><EmergencyNotice /></Reveal>

        <Reveal className="mt-10">
          <h2 className="text-2xl font-extrabold text-[hsl(var(--vc-ink))]">{t("urgent.examplesTitle")}</h2>
          <div className="mt-5 flex flex-wrap gap-2">
            {EXAMPLES.map((e) => (
              <span key={e} className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--vc-line))] bg-mint px-3.5 py-1.5 text-sm font-medium text-[hsl(var(--vc-ink))]">
                <Check className="h-3.5 w-3.5 text-brand" /> {e}
              </span>
            ))}
          </div>
        </Reveal>

        <Reveal className="mt-10">
          <h2 className="text-2xl font-extrabold text-[hsl(var(--vc-ink))]">{t("urgent.supportTitle")}</h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {SUPPORT_OPTIONS.map((s) => (
              <div key={s.title} className="rounded-2xl border border-[hsl(var(--vc-line))] bg-white p-5 vc-shadow-soft">
                <span className="grid h-10 w-10 place-items-center rounded-lg bg-mint text-brand"><s.icon className="h-5 w-5" /></span>
                <p className="mt-3 text-sm font-bold text-[hsl(var(--vc-ink))]">{s.title}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {URGENT_SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.05}>
              <ServiceCard icon={s.icon} title={s.title} text={s.short} to={`/urgent-care/${s.slug}`} />
            </Reveal>
          ))}
        </div>
      </section>

      <section className="vc-container pb-16 sm:pb-20">
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2"><Reveal><IndependenceStrip variant="band" /></Reveal></div>
          <Reveal delay={0.05}>
            <div className="flex h-full flex-col justify-center rounded-2xl border border-[hsl(var(--vc-line))] bg-white p-7 vc-shadow-soft">
              <h3 className="text-xl font-extrabold text-[hsl(var(--vc-ink))]">{t("urgent.notSureTitle")}</h3>
              <p className="mt-2 text-sm text-[hsl(var(--vc-slate))]">{t("urgent.notSureText")}</p>
              <Link to="/how-it-works" className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-brand">See how it works <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default UrgentCare;
